import type { DocNode } from '@atlaskit/adf-schema/doc';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { ADFEncoder } from '@atlaskit/renderer/utils';

import { isRemoveCitationsEnabled } from '../is-remove-citations-enabled';

import { AiMateMarkdownTransformer } from './md-tokenizer/markdown-transformer';

export type Content = DocNode | string;

export const isAdfContent = (content: Content): content is DocNode => {
	/*
	This try-catch is a necessary guard rail against broken content to prevent higher level
	error boundaries from displaying the error and breaking the experience.
	*/
	try {
		return typeof content !== 'string' && 'type' in content && content.type === 'doc';
	} catch {
		return false;
	}
};

export const appendContent = (
	content: string | DocNode,
	suggestionContent: string,
): string | DocNode => {
	if (isAdfContent(content)) {
		// Ensure the content is an array of nodes
		const updatedContent = Array.isArray(content.content) ? content.content : [content.content];

		// Append the new paragraph node
		updatedContent.push({
			type: 'paragraph',
			content: [
				{
					type: 'text',
					text: suggestionContent,
				},
			],
		});

		// Return the updated DocNode
		return {
			...content,
			content: updatedContent,
		};
	}
	return `${content}\n\n${suggestionContent}`;
};

// Default schema is https://bitbucket.org/atlassian/adf-schema/src/main/packages/adf-schema/src/schema/default-schema.ts
const adfEncoder = new ADFEncoder((schema) => new AiMateMarkdownTransformer(schema, false));

const streamingAdfEncoder = new ADFEncoder((schema) => new AiMateMarkdownTransformer(schema, true));

export const encodeAdf = (content: Content, streaming: boolean = false): DocNode => {
	if (isAdfContent(content)) {
		return content;
	}

	const CITATION_REGEX = /\[\^(\d+)\^\]/g;
	const INLINE_CITATION_REGEX = /\s?\[\^(\d+)\^\]/g;
	const transformedContent = isRemoveCitationsEnabled()
		? content.replace(CITATION_REGEX, '')
		: content;

	if (fg('csm_ai_enable_citations_in_widget')) {
		const stripped = transformedContent.replace(INLINE_CITATION_REGEX, '');

		// Currently csm-disable-unused-md-plugins hard coded to TRUE in widget = always disable
		// md.use(mdPluginSmartLinks) in MarkdownTransformer
		if (fg('csm-disable-unused-md-plugins')) {
			return streamingAdfEncoder.encode(stripped);
		}

		return streaming ? streamingAdfEncoder.encode(stripped) : adfEncoder.encode(stripped);
	}

	if (fg('csm-disable-unused-md-plugins')) {
		return streamingAdfEncoder.encode(transformedContent);
	}

	return streaming
		? streamingAdfEncoder.encode(transformedContent)
		: adfEncoder.encode(transformedContent);
};
